import React, { useState } from "react";
import "./StatusLabel.css";
import { ChevronDown, ChevronUp } from "lucide-react";
import { CompanyStatus } from "../../../../../../types/CompanyStatuses";
import { useChat } from "../../../../Layout/ChatProvider/ChatProvider";

interface StatusLabelProps {
    companyStatus: CompanyStatus; // Pass the full companyStatus object
    companyId: number; // Pass companyId
    onChange: (newStatus: CompanyStatus, companyId: number) => void; // Update onChange to pass companyId
}

const StatusLabel: React.FC<StatusLabelProps> = ({ companyStatus, companyId, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Access companyStatuses and companies from context
    const { companyStatuses, companies, setCompanies } = useChat();

    // Function to calculate luminance of a color
    const getLuminance = (color: string) => {
        const rgb = color.match(/\w\w/g)?.map(x => parseInt(x, 16)); // Extract RGB from hex
        if (!rgb) return 1; // Return a default luminance value if the color is invalid

        const [r, g, b] = rgb.map(val => val / 255);
        const a = [r, g, b].map(val => (val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)));
        return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2]; // Standard luminance formula
    };

    // Determine if the text should be light or dark based on the background color
    const luminance = getLuminance(companyStatus.color);
    const textColor = luminance < 0.5 ? "#FFF" : "#0C4068"; // If luminance is low (dark background), use white text

    const handleSelect = (selectedStatus: CompanyStatus) => {
        // Update the status of the company in the context
        const updatedCompanies = companies.map((company) => {
            if (Number(company.id) === companyId) {
                return {
                    ...company,
                    status: selectedStatus, // Update the status
                    operator_user_id: selectedStatus.operator_user_id.toString()
                };
            }
            return company;
        });
        console.log("selectedStatus OUID", selectedStatus.operator_user_id);
        setCompanies(updatedCompanies); // Update companies list in the context

        // Call the onChange function to pass the new status and companyId
        onChange(selectedStatus, companyId);
        setIsOpen(false); // Close the dropdown
    };

    return (
        <div className="status-label-container">
            <div
                className="status-label"
                onClick={() => setIsOpen((prev) => !prev)} // Toggle dropdown
                style={{
                    backgroundColor: companyStatus.color,  // Set background color
                    color: textColor          // Set dynamic text color
                }}
            >
                {isOpen ? <ChevronUp size={16} style={{ marginLeft: 6 }} /> : <ChevronDown size={16} style={{ marginLeft: 6 }} />}
                {companyStatus.status_name} {/* Display current status_name */}
            </div>

            {isOpen && (
                <div className="status-dropdown">
                    {/* Map over companyStatuses from context and render each as an option */}
                    {companyStatuses.map((status) => (
                        <div
                            key={status.status}
                            className={`status-option ${companyStatus.status === status.status ? "selected" : ""}`}
                            onClick={() => handleSelect(status)} // Pass the whole companyStatus object
                            style={{
                                backgroundColor: status.color,
                                color: getLuminance(status.color) < 0.5 ? "#FFF" : "#0C4068"
                            }}
                        >
                            {status.status_name} {/* Display the status name from companyStatuses */}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StatusLabel;
